/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * useCountries Hook
 * ============================================================================
 *
 * Provides localized country data by combining the base country information
 * (codes, dial codes, flags) with browser-native Intl.DisplayNames for
 * localized country names.
 *
 * This approach:
 * - Keeps stable data (ISO codes, dial codes, flags) in countries.ts
 * - Uses Intl.DisplayNames for zero-maintenance localized country names
 * - Automatically updates when language changes
 * - Maintains type safety
 *
 * ============================================================================
 */

import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  countries as baseCountries,
  type Country,
  type CountryConfig,
  getCountryConfig as getBaseCountryConfig,
} from '@/data/countries';

export interface LocalizedCountry extends Country {
  /** Localized country name */
  localizedName: string;
}

/**
 * Hook that returns country data with localized names
 */
export function useCountries() {
  const { i18n } = useTranslation('common');

  const countries = useMemo<LocalizedCountry[]>(() => {
    const displayNames = new Intl.DisplayNames([i18n.language], { type: 'region' });
    return baseCountries.map(country => ({
      ...country,
      localizedName: displayNames.of(country.code) || country.name,
    }));
  }, [i18n.language]);

  /**
   * Get localized country by ISO code
   */
  const getCountryByCode = useCallback((code: string): LocalizedCountry | undefined => {
    return countries.find(c => c.code === code);
  }, [countries]);

  /**
   * Get localized country by name (searches both localized and original names)
   */
  const getCountryByName = useCallback((name: string): LocalizedCountry | undefined => {
    const lowerName = name.toLowerCase();
    return countries.find(
      c => c.localizedName.toLowerCase() === lowerName || c.name.toLowerCase() === lowerName
    );
  }, [countries]);

  /**
   * Search countries by localized name (partial match)
   */
  const searchCountries = useCallback((query: string): LocalizedCountry[] => {
    if (!query.trim()) return countries;
    const lowerQuery = query.toLowerCase();
    return countries.filter(
      c =>
        c.localizedName.toLowerCase().includes(lowerQuery) ||
        c.name.toLowerCase().includes(lowerQuery) ||
        c.code.toLowerCase().includes(lowerQuery)
    );
  }, [countries]);

  /**
   * Get country-specific configuration (tax labels, placeholders, etc.)
   */
  const getCountryConfig = (countryCode: string): CountryConfig => {
    return getBaseCountryConfig(countryCode);
  };

  /**
   * Get options formatted for select components
   */
  const countryOptions = useMemo(() => {
    return countries.map(c => ({
      value: c.name, // Use original name as value for backend compatibility
      label: c.localizedName,
      icon: c.flag,
      description: c.dialCode,
      code: c.code,
    }));
  }, [countries]);

  /**
   * Get options using country code as value
   */
  const countryCodeOptions = useMemo(() => {
    return countries.map(c => ({
      value: c.code,
      label: c.localizedName,
      icon: c.flag,
      description: c.dialCode,
    }));
  }, [countries]);

  return {
    countries,
    countryOptions,
    countryCodeOptions,
    getCountryByCode,
    getCountryByName,
    searchCountries,
    getCountryConfig,
  };
}

export default useCountries;
